import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtEuro, MONTHS_FR, fmtDate } from "@/lib/format";
import { PeriodSelector } from "@/components/PeriodSelector";
import { BankCashFlow } from "@/components/BankCashFlow";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Legend,
} from "recharts";
import { ArrowUpRight, ArrowDownRight, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { InfoHint } from "@/components/InfoHint";

const PIE_COLORS = ["#22293A", "#C2492A", "#1F6F5C", "#8B8577", "#E0663F", "#4E5262", "#6D7182", "#A93D22", "#CFC6B6"];

export default function Dashboard() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/dashboard?year=${year}&month=${month}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  };

  useEffect(load, [year, month]);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Tableau de Bord</h1>
          <p className="text-muted-foreground text-sm mt-1">{MONTHS_FR[month - 1]} {year}</p>
        </div>
        <div className="flex items-center gap-2">
          <PeriodSelector year={year} month={month} setYear={setYear} setMonth={setMonth} />
        </div>
      </div>

      {loading || !data ? (
        <div className="h-64 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <>
          <BankCashFlow year={year} month={month} />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <Card className="lg:col-span-8 p-5 border-border">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="font-display font-semibold text-base">Revenus vs Dépenses — {year}</h3>
                <InfoHint testid="chart-revdep-info" text="Barres foncées = déjà réalisé chaque mois. Barres claires empilées au-dessus = ce qu'il reste de PRÉVU (budget mensuel + provisions exceptionnelles) non encore réalisé. Hauteur totale = montant attendu sur le mois. Hors virements internes." />
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.monthly_series} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={48} />
                    <Tooltip formatter={(v) => fmtEuro(v)} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", fontSize: 13 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="revenus" name="Revenus réalisés" stackId="rev" fill="#1F6F5C" />
                    <Bar dataKey="revenus_prevus" name="Revenus prévus (reste)" stackId="rev" fill="#9FCFC0" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="depenses" name="Dépenses réalisées" stackId="dep" fill="#C2492A" />
                    <Bar dataKey="depenses_prevues" name="Dépenses prévues (reste)" stackId="dep" fill="#E9B9AB" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="lg:col-span-4 p-5 border-border">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="font-display font-semibold text-base">Répartition par poste</h3>
                <InfoHint testid="chart-poste-info" text="Répartition de TOUTES les dépenses du mois par poste (catégories regroupées), y compris l'exceptionnel (« Prévisions annuelles »). Le total correspond aux « Dépenses » du graphe de gauche." />
              </div>
              {data.group_breakdown.length === 0 ? (
                <p className="text-sm text-muted-foreground py-12 text-center">Aucune dépense ce mois-ci.</p>
              ) : (
                <>
                  <div className="h-44">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={data.group_breakdown} dataKey="amount" nameKey="group" innerRadius={45} outerRadius={72} paddingAngle={2}>
                          {data.group_breakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => fmtEuro(v)} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", fontSize: 13 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-3 space-y-1.5">
                    {data.group_breakdown.slice(0, 5).map((g, i) => (
                      <div key={g.group} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2 text-muted-foreground">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                          {g.group}
                        </span>
                        <span className="font-num">{fmtEuro(g.amount)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <Card className="lg:col-span-7 p-5 border-border">
              <div className="flex items-center justify-between mb-4">
                <span className="flex items-center gap-2"><h3 className="font-display font-semibold text-base">Budget vs Réel — {MONTHS_FR[month - 1]}</h3><InfoHint testid="chart-budget-info" text="Suivi de vos budgets MENSUELS courants uniquement (mois type + ajustements). Les dépenses exceptionnelles ne sont PAS comptées ici (voir Vue Annuelle et widget Trésorerie). Reste à dépenser = budgets mensuels prévus − déjà dépensé (hors exceptionnel) ; c'est la somme des « reste » de chaque catégorie ci-dessous." /></span>
                <span className="text-sm">Reste à dépenser : <span className={`font-num font-semibold ${data.remaining_to_spend < 0 ? "text-destructive" : "text-emerald-600"}`} data-testid="dashboard-remaining">{fmtEuro(data.remaining_to_spend)}</span></span>
              </div>
              <div className="space-y-4">
                {data.category_progress.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">Aucun budget suivi pour ce mois.</p>
                ) : data.category_progress.slice(0, 8).map((c) => {
                  const pct = c.planned > 0 ? Math.min((c.spent / c.planned) * 100, 100) : 0;
                  const over = c.planned > 0 && c.spent > c.planned;
                  return (
                    <div key={c.category}>
                      <div className="flex items-center justify-between text-sm mb-1.5">
                        <span className="font-medium">{c.category}</span>
                        <span className="font-num text-muted-foreground">
                          {fmtEuro(c.spent)} <span className="opacity-50">/ {fmtEuro(c.planned)}</span>
                        </span>
                      </div>
                      <Progress value={pct} className={over ? "[&>div]:bg-destructive" : ""} />
                      <div className={`text-xs font-num mt-1 ${c.remaining < 0 ? "text-destructive" : "text-muted-foreground"}`}>{c.remaining < 0 ? `Dépassé de ${fmtEuro(-c.remaining)}` : `Reste à dépenser ${fmtEuro(c.remaining)}`}</div>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card className="lg:col-span-5 p-5 border-border">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="font-display font-semibold text-base">Mouvements récents</h3>
                <InfoHint testid="chart-recent-info" text="Vos 8 derniers mouvements du mois sélectionné, tous types confondus (dépenses, revenus, exceptionnels)." />
              </div>
              <div className="space-y-2">
                {data.recent_transactions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">Aucun mouvement.</p>
                ) : data.recent_transactions.map((t) => (
                  <div key={t.id} className="flex items-center gap-3 py-2 border-b border-border/60 last:border-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${t.type === "income" ? "bg-emerald-50 text-emerald-600" : "bg-orange-50 text-orange-600"}`}>
                      {t.type === "income" ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{t.vendor || t.description}</div>
                      <div className="text-xs text-muted-foreground truncate">{t.category_name} · {fmtDate(t.date)}</div>
                    </div>
                    <div className={`font-num text-sm font-medium ${t.type === "income" ? "text-emerald-600" : ""}`}>
                      {t.type === "income" ? "+" : "-"}{fmtEuro(t.amount)}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <Card className="p-5 border-border" data-testid="region-breakdown">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="font-display font-semibold text-base">Répartition par membre — {MONTHS_FR[month - 1]}</h3>
              <InfoHint testid="chart-member-info" text="Répartition de toutes les dépenses du mois par membre. Une dépense « toute la famille » est partagée à parts égales entre les membres." />
            </div>
            {(!data.member_breakdown || data.member_breakdown.length === 0) ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Ajoutez des membres de la famille (Espace Couple) pour répartir les dépenses. Les dépenses "toute la famille" sont partagées équitablement.</p>
            ) : (
              <div className="space-y-3" data-testid="member-breakdown">
                {data.member_breakdown.map((r) => {
                  const max = data.member_breakdown[0].amount || 1;
                  return (
                    <div key={r.member_id} data-testid={`member-row-${r.member_id}`}>
                      <div className="flex justify-between text-sm mb-1"><span className="font-medium flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: r.color }} />{r.name}</span><span className="font-num">{fmtEuro(r.amount)}</span></div>
                      <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-2 rounded-full" style={{ width: `${Math.max((r.amount / max) * 100, 4)}%`, backgroundColor: r.color }} /></div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <Card className="p-5 border-border" data-testid="region-breakdown-card">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="font-display font-semibold text-base">Répartition par région — {MONTHS_FR[month - 1]}</h3>
              <InfoHint testid="chart-region-info" text="Répartition des dépenses du mois par région, déduite de la localisation de vos relevés importés." />
            </div>
            {(!data.region_breakdown || data.region_breakdown.length === 0) ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Aucune dépense localisée ce mois-ci. Importez un relevé de carte pour voir où ont eu lieu vos dépenses.</p>
            ) : (
              <div className="space-y-3">
                {data.region_breakdown.map((r) => {
                  const max = data.region_breakdown[0].amount || 1;
                  return (
                    <div key={r.region} data-testid={`region-row-${r.region}`}>
                      <div className="flex justify-between text-sm mb-1"><span className="font-medium">{r.region}</span><span className="font-num">{fmtEuro(r.amount)}</span></div>
                      <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-2 rounded-full bg-primary" style={{ width: `${Math.max((r.amount / max) * 100, 4)}%` }} /></div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
